"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  sortOptions,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  PieChart,
  type SliceLabelMode,
} from "@/components/tool-kit/charts/pie-chart";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import type { SortOrder } from "@/lib/tool-kit/charts/bars";
import { MAX_SERIES } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  percentText,
  valueText,
} from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { parseLabeledRows } from "@/lib/tool-kit/charts/parse";
import {
  layoutPie,
  type PieItem,
  type PieLayout,
} from "@/lib/tool-kit/charts/pie";

/** English strings. A translated content file overrides these through `ui`. */
export const PIE_CHART_MAKER_STRINGS = {
  dataLabel: "Your data — one slice per line: label, value",
  dataHint:
    "Separate the label and the value with a comma, tab or semicolon (use semicolons if your numbers have decimal commas). Counts or percentages both work; a first line without numbers is treated as a header.",
  exampleData: "Transport, Students\nWalk, 42\nBus, 31\nCar, 18\nBike, 9",
  firstLineHeader: "The first line names the columns",
  exampleTitle: "How Surveyed Students Travel to Campus",
  slicesDetected: "one:{count} slice|other:{count} slices",
  typeLabel: "Chart type",
  pie: "Pie",
  donut: "Donut (ring)",
  sliceLabelsLabel: "Labels on the slices",
  labelPercent: "Percentages",
  labelValue: "Values",
  labelBoth: "Values and percentages",
  labelNone: "None",
  sortLabel: "Order of the slices",
  sortNone: "As entered",
  sortAscending: "Smallest first",
  sortDescending: "Largest first",
  groupSmall: "Combine small slices into “Other”",
  thresholdLabel: "Combine slices smaller than (%)",
  otherLabel: "Other",
  percentagesCheck: "My values are percentages of one whole",
  total: "Total",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  colorLabel: "Color of the first slice",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above the chart — the APA 7 layout. The legend stays inside the image, where APA puts it.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  categoryColumn: "Category",
  valueColumn: "Value",
  percentColumn: "Percent of total",
  chartLabel: "Pie chart",
  chartDescription:
    "Pie chart of {slices} totaling {total}. Largest: {maxLabel}, {maxShare}. Smallest: {minLabel}, {minShare}.",
  enterData:
    "Enter at least one line with a label and a positive number to draw the chart.",
  dropped:
    "one:Left out {labels} — a slice cannot be zero or negative.|other:Left out {labels} — slices cannot be zero or negative.",
  otherContents: "“Other” combines {labels}.",
  capped:
    "Only eight slices get their own color, so the smallest categories were combined into “Other”.",
  tooManySlices:
    "With {count} slices, the small ones are hard to compare. A bar graph shows these differences more accurately.",
  percentSum:
    "These percentages add up to {sum}%, not 100%. If people could choose more than one answer, the parts overlap and do not form one whole — use a bar graph instead.",
  singleSlice:
    "A pie with one slice is a full circle — there is nothing to compare. Add more categories, or report the number in the text.",
  onlyFirstColumn:
    "Only the first column of values is drawn — a pie chart shows one whole. For several series, use a bar graph.",
} as const;

type Format = NumberFormat;

/** The notices under the data: what was left out, merged, or looks wrong. */
function pieNotices(
  pie: PieLayout,
  options: {
    percentages: boolean;
    groupRequested: boolean;
    extraColumns: boolean;
  },
  strings: typeof PIE_CHART_MAKER_STRINGS,
  locale: string,
  fmt: Format
): string[] {
  const notes: string[] = [];
  if (options.extraColumns) {
    notes.push(strings.onlyFirstColumn);
  }
  if (pie.dropped.length > 0) {
    notes.push(
      tPlural(strings.dropped, locale, pie.dropped.length, {
        labels: pie.dropped.map((item) => item.label).join(", "),
      })
    );
  }
  if (pie.grouped.length > 0) {
    if (!options.groupRequested) {
      notes.push(strings.capped);
    }
    notes.push(
      t(strings.otherContents, {
        labels: pie.grouped.map((item) => item.label).join(", "),
      })
    );
  }
  if (pie.slices.length === 1) {
    notes.push(strings.singleSlice);
  } else if (pie.slices.length > 6) {
    notes.push(t(strings.tooManySlices, { count: fmt(pie.slices.length) }));
  }
  // The slices actually drawn: a dropped zero or negative value is not part of the whole.
  const sum = pie.total;
  if (options.percentages && Math.abs(sum - 100) > 0.5) {
    notes.push(t(strings.percentSum, { sum: valueText(fmt, sum) }));
  }
  return notes;
}

export default function PieChartMakerWidget() {
  const strings = useToolUi(PIE_CHART_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [donut, setDonut] = useState(false);
  const [labelMode, setLabelMode] = useState<SliceLabelMode>("percent");
  const [sort, setSort] = useState<SortOrder>("none");
  const [group, setGroup] = useState(false);
  const [threshold, setThreshold] = useState("5");
  const [percentages, setPercentages] = useState(false);
  const [headerChoice, setHeaderChoice] = useState<boolean | null>(null);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: true,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseLabeledRows(raw, { header: headerChoice ?? undefined });
  const items: PieItem[] = table.rows
    .filter((row) => row.values[0] !== null)
    .map((row) => ({ label: row.label, value: row.values[0] as number }));
  const cut = parseNumberInput(threshold);
  const pie = layoutPie(items, {
    sort,
    otherLabel: strings.otherLabel,
    otherBelow: group && cut !== null && cut > 0 ? cut / 100 : null,
    maxSlices: MAX_SERIES,
  });
  const notices = pieNotices(
    pie,
    { percentages, groupRequested: group, extraColumns: table.seriesCount > 1 },
    strings,
    locale,
    fmt
  );
  const largest = pie.slices.reduce(
    (a, b) => (b.value > a.value ? b : a),
    pie.slices[0]
  );
  const smallest = pie.slices.reduce(
    (a, b) => (b.value < a.value ? b : a),
    pie.slices[0]
  );
  const share = (value: number) => percentText(fmt, value);
  const positive = items.filter((item) => item.value > 0);
  const header = [
    table.header?.[0]?.trim() || strings.categoryColumn,
    table.header?.[1]?.trim() || strings.valueColumn,
  ];

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="pcm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={7}
        value={raw}
      />
      <Checkbox
        checked={headerChoice ?? table.autoHeader}
        label={strings.firstLineHeader}
        onChange={setHeaderChoice}
      />
      <div aria-live="polite" className="space-y-2">
        {pie.slices.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.slicesDetected, locale, pie.slices.length, {
              count: fmt(pie.slices.length),
            })}
          </p>
        ) : (
          <Notice>{strings.enterData}</Notice>
        )}
        {notices.map((notice) => (
          <Notice key={notice}>{notice}</Notice>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.typeLabel}
          name="pcm-type"
          onChange={(value) => setDonut(value === "donut")}
          options={[
            { value: "pie", label: strings.pie },
            { value: "donut", label: strings.donut },
          ]}
          value={donut ? "donut" : "pie"}
        />
        <SelectField
          id="pcm-labels"
          label={strings.sliceLabelsLabel}
          onChange={setLabelMode}
          options={[
            { value: "percent", label: strings.labelPercent },
            { value: "value", label: strings.labelValue },
            { value: "both", label: strings.labelBoth },
            { value: "none", label: strings.labelNone },
          ]}
          value={labelMode}
        />
      </div>
      {pie.slices.length > 0 ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([
            header,
            ...items.map((item) => [item.label, item.value]),
          ])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "pie-chart")}
          labels={strings}
          note={style.note}
          table={{
            head: [...header, strings.percentColumn],
            rows: positive.map((item) => [
              item.label,
              valueText(fmt, item.value),
              share(item.value / pie.total),
            ]),
          }}
          title={style.title}
        >
          <PieChart
            description={t(strings.chartDescription, {
              slices: tPlural(
                strings.slicesDetected,
                locale,
                pie.slices.length,
                { count: fmt(pie.slices.length) }
              ),
              total: valueText(fmt, pie.total),
              maxLabel: largest.label,
              maxShare: share(largest.share),
              minLabel: smallest.label,
              minShare: share(smallest.share),
            })}
            donut={donut}
            fmt={fmt}
            id="pcm-chart"
            label={style.title.trim() || strings.chartLabel}
            labelMode={labelMode}
            pie={pie}
            style={style}
            totalLabel={strings.total}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        colorLabels={strings}
        idPrefix="pcm"
        labels={strings}
        style={style}
        update={update}
      >
        <SelectField
          id="pcm-sort"
          label={strings.sortLabel}
          onChange={setSort}
          options={sortOptions(strings)}
          value={sort}
        />
        <div className="space-y-3">
          <Checkbox
            checked={group}
            label={strings.groupSmall}
            onChange={setGroup}
          />
          {group ? (
            <TextField
              id="pcm-threshold"
              label={strings.thresholdLabel}
              numeric
              onChange={setThreshold}
              value={threshold}
            />
          ) : null}
          <Checkbox
            checked={percentages}
            label={strings.percentagesCheck}
            onChange={setPercentages}
          />
        </div>
      </ChartStyleFields>
    </div>
  );
}
